import { useEffect, useMemo, useState } from 'react'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { type Family, classify } from '@viewer/lib/entityFamily'

interface EntityRow {
  ifcEntity: string // canonical name, e.g. "IFCWALL"
  count: number
  family: Family
}


// Famílias de IfcElement (MEP, estrutura, equipamento) reconhecíveis
// pelo sufixo do nome, para as entidades fora da tabela de herança.
const FAMILY_LABEL: Record<Family, string> = {
  spatial: 'IfcSpatialStructureElement (estrutura espacial)',
  element: 'IfcElement (elementos físicos)',
  group: 'IfcGroup (agregações lógicas)',
  type: 'IfcTypeObject (tipos)',
  relationship: 'IfcRelationship (relações objectivadas)',
  propertyDef: 'IfcPropertyDefinition (psets / qsets)',
  resource: 'Recursos (materiais, unidades, estilos)',
  other: 'Outros',
}

const FAMILY_ORDER: Family[] = [
  'spatial',
  'element',
  'group',
  'type',
  'other',
  'relationship',
  'propertyDef',
  'resource',
]

/** Uma só lista vazia, partilhada: devolver `[]` novo a cada render
 *  fazia o useMemo do agrupamento correr sempre. */
const EMPTY_ROWS: EntityRow[] = []

function EntitiesBrowser() {
  const activeModelId = useAppStore((s) => s.activeModelId)
  // A lista guarda consigo o modelo de que veio. Assim fica velha
  // sozinha quando o modelo muda, em vez de ser preciso limpá-la no
  // corpo do efeito: escrever estado aí encadeia renders, e era isso
  // que o lint apontava.
  const [carga, setCarga] = useState<{
    modelId: string | null
    rows: EntityRow[]
  }>({ modelId: null, rows: [] })
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState<Set<Family>>(new Set())

  const actual = carga.modelId === activeModelId
  const rows = actual ? carga.rows : EMPTY_ROWS
  const loading = Boolean(activeModelId) && !actual

  useEffect(() => {
    if (!activeModelId) return
    let cancelled = false
    void (async () => {
      const model = viewerApi.getModel(activeModelId)
      if (!model) return
      try {
        // Fragments answers a wildcard regex with every entity it
        // knows. The result is keyed by the canonical UPPERCASE
        // entity name (e.g. "IFCWALL").
        const bucket = await model.getItemsOfCategories([/.*/])
        if (cancelled) return
        const list: EntityRow[] = []
        for (const [ent, ids] of Object.entries(bucket)) {
          if (!ids || ids.length === 0) continue
          list.push({
            ifcEntity: ent,
            count: ids.length,
            family: classify(ent),
          })
        }
        list.sort((a, b) => a.ifcEntity.localeCompare(b.ifcEntity))
        setCarga({ modelId: activeModelId, rows: list })
      } catch (err) {
        console.warn('[EntitiesBrowser] getItemsOfCategories failed', err)
        // Marcar como carregado à mesma: senão fica a dizer "a ler…"
        // para sempre sobre um modelo que não se consegue ler.
        if (!cancelled) setCarga({ modelId: activeModelId, rows: [] })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activeModelId])

  const grouped = useMemo(() => {
    const m: Record<Family, EntityRow[]> = {
      spatial: [],
      element: [],
      group: [],
      type: [],
      relationship: [],
      propertyDef: [],
      resource: [],
      other: [],
    }
    const term = search.trim().toLowerCase()
    for (const r of rows) {
      if (term && !r.ifcEntity.toLowerCase().includes(term)) continue
      m[r.family].push(r)
    }
    return m
  }, [rows, search])

  const selectAll = async (
    ifcEntity: string,
    mode: 'replace' | 'add',
  ) => {
    if (!activeModelId) return
    const model = viewerApi.getModel(activeModelId)
    if (!model) return
    try {
      const bucket = await model.getItemsOfCategories([
        new RegExp(`^${ifcEntity}$`, 'i'),
      ])
      const ids: number[] = []
      for (const arr of Object.values(bucket)) {
        if (arr) ids.push(...arr)
      }
      const targets = ids.map((id) => ({ modelId: activeModelId, localId: id }))
      useAppStore.getState().applyBulkSelection(targets, mode)
    } catch (err) {
      console.warn('[EntitiesBrowser] selectAll failed', err)
    }
  }

  const hideAll = async (ifcEntity: string) => {
    await selectAll(ifcEntity, 'replace')
    void viewerApi.hideSelected()
  }

  const isolateAll = async (ifcEntity: string) => {
    await selectAll(ifcEntity, 'replace')
    void viewerApi.isolateSelected()
  }

  if (!activeModelId) {
    return (
      <div className="p-3 text-[11px] italic text-[var(--color-text-muted)]">
        Carrega um IFC para listar as entidades presentes.
      </div>
    )
  }
  if (loading) {
    return (
      <div className="p-3 text-[11px] italic text-[var(--color-text-muted)]">
        A indexar entidades…
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <div className="p-3 text-[11px] italic text-[var(--color-text-muted)]">
        Modelo sem entidades indexadas.
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-[var(--color-border-subtle)] p-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filtrar entidade IFC…"
          className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[11px] outline-none focus:border-[var(--color-accent)]"
        />
        <div className="mt-1 text-[9px] italic text-[var(--color-text-dim)]">
          Clique: substituir selecção · Ctrl/Cmd: somar
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2 text-[11px]">
        {FAMILY_ORDER.map((fam) => {
          const items = grouped[fam]
          if (items.length === 0) return null
          const isCollapsed = collapsed.has(fam)
          return (
            <div key={fam}>
              <button
                type="button"
                onClick={() =>
                  setCollapsed((prev) => {
                    const next = new Set(prev)
                    if (next.has(fam)) next.delete(fam)
                    else next.add(fam)
                    return next
                  })
                }
                className="flex w-full items-center justify-between rounded px-1 py-0.5 text-left text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
              >
                <span className="truncate">{FAMILY_LABEL[fam]}</span>
                <span className="ml-2 text-[var(--color-text-dim)]">
                  {isCollapsed ? '+' : '−'}
                </span>
              </button>
              {!isCollapsed && (
              <ul className="mt-0.5 flex flex-col gap-px">
                {items.map((r) => {
                  // Houve aqui um destaque das linhas que contêm a
                  // selecção. Nunca chegou a funcionar: a contagem
                  // vinha sempre a zero, com um comentário a dizer
                  // "saltar por agora", e o ramo colorido nunca
                  // corria. Ficou só a borda transparente, que é o
                  // que se via mesmo.
                  const rowClass = 'border-l-2 border-transparent'
                  return (
                    <li key={r.ifcEntity} className="group">
                      <div
                        className={`grid w-full grid-cols-[1fr_auto] items-baseline gap-x-2 rounded px-1 py-0.5 hover:bg-[var(--color-bg)] ${rowClass}`}
                      >
                        <button
                          type="button"
                          onClick={(ev) =>
                            void selectAll(
                              r.ifcEntity,
                              ev.ctrlKey || ev.metaKey ? 'add' : 'replace',
                            )
                          }
                          className="truncate text-left font-mono text-[10px] text-[var(--color-text)]"
                          title={`Selecionar todas as instâncias de ${r.ifcEntity}`}
                        >
                          {r.ifcEntity}
                        </button>
                        <span className="text-right text-[10px] text-[var(--color-text-muted)]">
                          {r.count}
                        </span>
                      </div>
                      <div className="ml-1 flex gap-1 text-[9px] opacity-0 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => void hideAll(r.ifcEntity)}
                          title="Esconder todas as instâncias"
                          className="rounded px-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                        >
                          esconder
                        </button>
                        <button
                          type="button"
                          onClick={() => void isolateAll(r.ifcEntity)}
                          title="Isolar (esconder tudo o resto)"
                          className="rounded px-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                        >
                          isolar
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default EntitiesBrowser
